// Arrays of colours should have pillar colors first
// e.g. for sport:
// const [sport300, sport400, sport500, sport600, sport800] = colors.blue
import { tokens } from '@guardian/design-tokens';

/**
 * [Storybook](https://guardian.github.io/csnx/?path=/docs/source-foundations_palette--palette) •
 * [Design System](https://theguardian.design/2a1e5182b/p/492a30-light-palette)
 *
 * Colour palette organised by type e.g. 'error', or pillar e.g. 'opinion'
 */
export const palette = {
	brand: tokens.palette.brand,
	brandAlt: tokens.palette.brandAlt,
	neutral: tokens.palette.neutral,
	error: tokens.palette.error,
	success: tokens.palette.success,
	news: tokens.palette.news,
	opinion: tokens.palette.opinion,
	sport: tokens.palette.sport,
	culture: tokens.palette.culture,
	lifestyle: tokens.palette.lifestyle,
	labs: tokens.palette.labs,
	specialReport: tokens.palette.specialReport,
	specialReportAlt: tokens.palette.specialReportAlt,
	focus: tokens.palette.focus,
} as const;

// Hover colours are snowflakes as they are manipulations of colours from the
// main palette.

/**
 * [Storybook](https://guardian.github.io/csnx/?path=/docs/source-foundations_palette--palette) •
 * [Design System](https://theguardian.design/2a1e5182b/p/1377a6-tokens/t/9280ee)
 *
 * Default theme background colours
 *
 * @deprecated Use the `palette` export instead
 */
export const background = {
	primary: palette.neutral[100],
	secondary: palette.neutral[97],
	inverse: palette.neutral[10],
	ctaPrimary: palette.brand[400],
	ctaPrimaryHover: '#234B8A',
	ctaSecondary: palette.brand[800],
	ctaSecondaryHover: '#ACC9F7',
	ctaTertiaryHover: '#E5E5E5',
	input: palette.neutral[100],
	inputChecked: palette.brand[500],
} as const;

/**
 * [Storybook](https://guardian.github.io/csnx/?path=/docs/source-foundations_palette--palette) •
 * [Design System](https://theguardian.design/2a1e5182b/p/1377a6-tokens/t/63cc10)
 *
 * Brand theme background colours
 *
 * @deprecated Use the `palette` export instead
 */
export const brandBackground = {
	primary: palette.brand[400],
	inputChecked: palette.neutral[100],
	ctaPrimary: palette.neutral[100],
	ctaPrimaryHover: '#E0E0E0',
	ctaSecondary: palette.brand[600],
	ctaSecondaryHover: '#234B8A',
	ctaTertiaryHover: palette.brand[300],
} as const;

/**
 * [Storybook](https://guardian.github.io/csnx/?path=/docs/source-foundations_palette--palette) •
 * [Design System](https://theguardian.design/2a1e5182b/p/1377a6-tokens/t/743232)
 *
 * Alternative brand theme background colours
 *
 * @deprecated Use the `palette` export instead
 */
export const brandAltBackground = {
	primary: palette.brandAlt[400],
	ctaPrimary: palette.neutral[7],
	ctaPrimaryHover: '#454545',
	ctaSecondary: palette.brandAlt[200],
	ctaSecondaryHover: '#F2AE00',
	ctaTertiaryHover: '#FFD213',
} as const;

/**
 * [Storybook](https://guardian.github.io/csnx/?path=/docs/source-foundations_palette--palette) •
 * [Design System](https://theguardian.design/2a1e5182b/p/1377a6-tokens/t/69c92f)
 *
 * Default theme border colours
 *
 * @deprecated Use the `palette` export instead
 */
export const border = {
	primary: palette.neutral[60],
	secondary: palette.neutral[86],
	success: palette.success[400],
	error: palette.error[400],
	ctaTertiary: palette.brand[400],
	input: palette.neutral[60],
	inputChecked: palette.brand[500],
	inputHover: palette.brand[500],
	inputActive: palette.brand[500],
	focusHalo: palette.focus[400],
};

/**
 * [Storybook](https://guardian.github.io/csnx/?path=/docs/source-foundations_palette--palette) •
 * [Design System](https://theguardian.design/2a1e5182b/p/1377a6-tokens/t/04883b)
 *
 * Brand theme border colours
 *
 * @deprecated Use the `palette` export instead
 */
export const brandBorder = {
	primary: palette.brand[600],
	secondary: palette.brand[600],
	success: palette.success[500],
	error: palette.error[500],
	ctaTertiary: palette.neutral[100],
	input: palette.brand[800],
	inputChecked: palette.neutral[100],
	inputHover: palette.neutral[100],
};

/**
 * [Storybook](https://guardian.github.io/csnx/?path=/docs/source-foundations_palette--palette) •
 * [Design System](https://theguardian.design/2a1e5182b/p/1377a6-tokens/b/21c6cc)
 *
 * Alternative brand theme border colours
 *
 * @deprecated Use the `palette` export instead
 */
export const brandAltBorder = {
	ctaTertiary: palette.neutral[7],
};

/**
 * [Storybook](https://guardian.github.io/csnx/?path=/docs/source-foundations_palette--palette) •
 * [Design System](https://theguardian.design/2a1e5182b/p/1377a6-tokens/t/593cc9)
 *
 * Default theme line colours
 *
 * @deprecated Use the `palette` export instead
 */
export const line = {
	primary: palette.neutral[86],
};

/**
 * [Storybook](https://guardian.github.io/csnx/?path=/docs/source-foundations_palette--palette) •
 * [Design System](https://theguardian.design/2a1e5182b/p/1377a6-tokens/t/66455d)
 *
 * Brand theme line colours
 *
 * @deprecated Use the `palette` export instead
 */
export const brandLine = {
	primary: palette.brand[600],
};

/**
 * [Storybook](https://guardian.github.io/csnx/?path=/docs/source-foundations_palette--palette) •
 * [Design System](https://theguardian.design/2a1e5182b/p/1377a6-tokens/t/89955e)
 *
 * Alternative brand theme line colours
 *
 * @deprecated Use the `palette` export instead
 */
export const brandAltLine = {
	primary: palette.neutral[7],
};

/**
 * [Storybook](https://guardian.github.io/csnx/?path=/docs/source-foundations_palette--palette) •
 * [Design System](https://theguardian.design/2a1e5182b/p/1377a6-tokens/t/85d3b0)
 *
 * Default theme text colours
 *
 * @deprecated Use the `palette` export instead
 */
export const text = {
	primary: palette.neutral[7],
	supporting: palette.neutral[46],
	success: palette.success[400],
	error: palette.error[400],
	ctaPrimary: palette.neutral[100],
	ctaSecondary: palette.brand[400],
	ctaTertiary: palette.brand[400],
	anchorPrimary: palette.brand[500],
	anchorSecondary: palette.neutral[7],
	userInput: palette.neutral[7],
	inputLabel: palette.neutral[7],
	inputError: palette.neutral[7],
	inputLabelSupporting: palette.neutral[46],
	inputChecked: palette.brand[400], //choice card only
	inputHover: palette.brand[400], //choice card only
	groupLabel: palette.neutral[7],
	groupLabelSupporting: palette.neutral[46],
	newsInverse: palette.news[550],
};

/**
 * [Storybook](https://guardian.github.io/csnx/?path=/docs/source-foundations_palette--palette) •
 * [Design System](https://theguardian.design/2a1e5182b/p/1377a6-tokens/t/244480)
 *
 * Brand theme text colours
 *
 * @deprecated Use the `palette` export instead
 */
export const brandText = {
	primary: palette.neutral[100],
	supporting: palette.brand[800],
	success: palette.success[500],
	error: palette.error[500],
	ctaPrimary: palette.brand[400],
	ctaSecondary: palette.neutral[100],
	ctaTertiary: palette.neutral[100],
	anchorPrimary: palette.neutral[100],
	anchorPrimaryHover: palette.brandAlt[400],
	userInput: palette.neutral[100],
	inputLabel: palette.neutral[100],
	inputLabelSupporting: palette.brand[800],
};

/**
 * [Storybook](https://guardian.github.io/csnx/?path=/docs/source-foundations_palette--palette) •
 * [Design System](https://theguardian.design/2a1e5182b/p/1377a6-tokens/t/11d5fd)
 *
 * Alternative brand theme text colours
 *
 * @deprecated Use the `palette` export instead
 */
export const brandAltText = {
	primary: palette.neutral[7],
	supporting: palette.neutral[60],
	ctaPrimary: palette.neutral[100],
	ctaSecondary: palette.neutral[7],
	ctaTertiary: palette.neutral[7],
	anchorPrimary: palette.neutral[7],
};

export const {
	brand,
	brandAlt,
	neutral,
	error,
	success,
	news,
	opinion,
	sport,
	culture,
	lifestyle,
	labs,
	specialReport,
	specialReportAlt,
	focus,
} = palette;
